export type Language = "en" | "am";

export type TranslationKeys = keyof typeof en;

const en = {
  // Header
  "header.title": "Healthcare Data Analytics Agent",
  "header.subtitle": "Ask questions about your medical data and get insights",
  "btn.chat": "Chat",
  "btn.help": "Help",
  "btn.newChat": "New Chat",
  "chat.sidebar.chats": "Chats",
  "chat.sidebar.empty": "No conversations yet",
  "chat.clarification.choose": "Choose one",
  "btn.logout": "Log out",
  "btn.dash": "Anomaly",
  "btn.reports": "Report Generator",

  // Report Generator
  "reports.title": "Report Generator",
  "reports.subtitle": "Generate and export healthcare data reports",
  "reports.form.title": "Report Details",
  "reports.form.reportName": "Report Name",
  "reports.form.reportType": "Report Type",
  "reports.form.startDate": "Start Date",
  "reports.form.endDate": "End Date",
  "reports.form.language": "Language",
  "reports.form.format": "Export Format",
  "reports.language.en": "English",
  "reports.language.am": "Amharic",
  "header.lang.en": "EN",
  "header.lang.am": "Amharic",
  "reports.format.docx": "DOCX",
  "reports.format.pptx": "PPTX",
  "reports.download.docx": "Download DOCX",
  "reports.download.pptx": "Download PPTX",
  "reports.sidebar.templateSelection": "Template Selection",
  "reports.sidebar.parameters": "Parameters",
  "reports.sidebar.geography": "Geography",
  "reports.sidebar.aiGuidance": "AI Guidance",
  "reports.sidebar.month": "Month",
  "reports.sidebar.quarter": "Quarter",
  "reports.sidebar.year": "Year",
  "reports.sidebar.region": "Region",
  "reports.sidebar.national": "National",
  "reports.sidebar.regional": "Regional",
  "reports.sidebar.nationalLevel": "National Level",
  "reports.preview.readyTitle": "Ready to Generate Report",
  "reports.preview.readyDesc":
    "Configure the parameters and click \"Generate Report\" to preview the document.",
  "reports.recent.title": "Recently generated",
  "reports.recent.empty": "Your last 3 reports will appear here after you generate one.",
  "reports.recent.sections": "sections",
  "reports.recent.open": "Open",
  "reports.form.generate": "Generate Report",
  "reports.errors.periodTitle": "Requested reporting period is unavailable",
  "reports.errors.geographyTitle": "Requested reporting geography is unavailable",
  "reports.errors.generationTitle": "Report generation failed",
  "reports.errors.conflictTitle": "Generated metadata contradicts the request",
  "reports.errors.generationFailed": "The report could not be generated.",
  "reports.errors.headerUnavailable": "Reporting period unavailable",
  "reports.errors.periodUnavailable": "Period unavailable",
  "reports.errors.geographyUnavailable": "Geography unavailable",
  "reports.type.malaria": "Malaria Trends",
  "reports.type.hiv": "HIV Statistics",
  "reports.type.tb": "Tuberculosis Summary",
  "reports.type.regional": "Regional Comparison",
  "btn.stop": "Stop",

  // Welcome
  "welcome.greeting.morning": "Good morning",
  "welcome.greeting.afternoon": "Good afternoon",
  "welcome.greeting.evening": "Good evening",
  "welcome.heading": "Welcome to Healthcare Data Analytics Agent",
  "welcome.description":
    "Your intelligent healthcare insights companion. Ask me anything about disease trends, regional health statistics, patient demographics, or any insights you need to improve healthcare outcomes.",

  // Feature highlights
  "feature.analytics.title": "Real-time Analytics",
  "feature.analytics.desc": "Get instant insights from your medical data",
  "feature.viz.title": "Smart Visualizations",
  "feature.viz.desc": "Automatic charts and graphs",
  "feature.patient.title": "Patient Insights",
  "feature.patient.desc":
    "Understand patient demographics and health trends better",

  // Suggestion queries
  "suggestions.heading": "Try these popular queries",
  "suggestion.1.category": "Immunization",
  "suggestion.1.text": "Full immunization coverage by region for the latest year.",
  "suggestion.2.category": "Maternal Healthcare",
  "suggestion.2.text":
    "Skilled birth attendance rate by region.",
  "suggestion.3.category": "ANC",
  "suggestion.3.text": "Women with ANC 4+ visits by region.",

  // Loading
  "loading.title": "Analyzing your query...",
  "loading.subtitle": "This may take a few moments",

  // Chat input
  "input.placeholder":
    "Ask about immunization, maternal health, or ANC indicators...",
  "input.suggestion.1":
    "Full immunization coverage by region in 2018 fiscal year",
  "input.suggestion.2": "Penta 3 and measles 1st dose coverage trends",
  "input.suggestion.3": "ANC first visit coverage by region",
  "input.suggestion.4": "ANC 4+ visits by facility",
  "input.suggestion.5": "Maternal healthcare indicators at national level",
  "input.suggestion.6": "Compare immunization coverage across regions",
  "input.suggestion.7": "Malaria cases by region for the latest year",
  "input.suggestion.8": "HIV reportings trend over the last 12 months",
  "input.suggestion.9": "TB treatment success rate by region",
  "input.disclaimer": "AI-generated responses may contain errors. Please verify data and sources before making decisions.",

  // Starter prompts (empty state)
  "starters.heading": "Start with a question",
  "starters.scope":
    "Covers Ethiopian malaria, HIV, TB, immunization and maternal health indicators by region, facility and fiscal year.",
  "starters.theme.immunization": "Immunization coverage",
  "starters.theme.maternal": "Maternal & antenatal care",
  "starters.theme.diseases": "Malaria, HIV & TB",

  // Table / Chart labels
  "table.title": "Query Results",
  "table.results": "Results",

  // Support page
  "support.title": "Healthcare Data Analytics Agent - User Guide",
  "support.subtitle":
    "Learn how to get the most from your health data analysis",
  "support.backToChat": "Back to Chat",

  "support.gettingStarted": "Getting Started",
  "support.howToUse": "How to Use the System",
  "support.step1.title": "Ask Your Question",
  "support.step1.desc":
    "Type your health data question in natural language in the chat box.",
  "support.step2.title": "Get AI Analysis",
  "support.step2.desc":
    "The system analyzes your query and generates appropriate visualizations.",
  "support.step3.title": "Explore Results",
  "support.step3.desc":
    "View charts, tables, and AI explanations. Ask follow-up questions for deeper insights.",

  "support.context.title": "Ethiopian Health Data Context",
  "support.context.intro":
    "This system contains health data from Ethiopian health facilities, covering:",
  "support.context.diseases":
    "Diseases: HIV/AIDS, Tuberculosis, Malaria, and other conditions",
  "support.context.regions":
    "Regions: Amhara, Oromia, Tigray, Addis Ababa, and all Ethiopian regions",
  "support.context.facilities":
    "Facilities: Hospitals, Health Centers, Health Posts, and Clinics",
  "support.context.indicators":
    "Indicators: Treatment coverage, case notifications, positivity rates",

  "support.vizTypes": "Visualization Types",
  "support.chart.bar": "Bar Charts",
  "support.chart.bar.desc":
    "Compare categories like regions, facilities, or disease types",
  "support.chart.bar.example": "Number of Malaria cases by region",
  "support.chart.line": "Line Charts",
  "support.chart.line.desc": "Show trends over time periods",
  "support.chart.line.example": "Monthly TB cases over 2016",
  "support.chart.pie": "Pie Charts",
  "support.chart.pie.desc": "Display proportions and percentages",
  "support.chart.pie.example": "Share of facilities by ownership type",
  "support.chart.table": "Tables",
  "support.chart.table.desc": "Raw data display with sorting and filtering",
  "support.chart.table.example": "List of all health facilities in Oromia",
  "support.chart.kpi": "KPI Cards",
  "support.chart.kpi.desc": "Single key metrics and totals",
  "support.chart.kpi.example": "Total number of health facilities",

  "support.examples": "Example Queries",

  "support.cat.regional": "Regional Analysis",
  "support.cat.regional.q1": "Show me all health facilities in Amhara region",
  "support.cat.regional.q2": "Number of Malaria cases by region",
  "support.cat.regional.q3": "Compare TB indicators across all regions",
  "support.cat.regional.q4": "Which region has the most health centers?",

  "support.cat.time": "Time-based Analysis",
  "support.cat.time.q1": "Monthly HIV cases in 2016",
  "support.cat.time.q2":
    "Trend of Malaria positivity rate over the last year",
  "support.cat.time.q3": "Quarter-over-quarter comparison of TB cases",
  "support.cat.time.q4": "Show health indicators by year",

  "support.cat.indicators": "Health Indicators",
  "support.cat.indicators.q1": "List all available health indicators",
  "support.cat.indicators.q2": "HIV treatment coverage by facility",
  "support.cat.indicators.q3": "Malaria positivity rates in Oromia",
  "support.cat.indicators.q4": "TB notification rates by zone",

  "support.tips": "Tips for Better Results",
  "support.tip1.title": "Be Specific",
  "support.tip1.desc":
    "Include specific regions, time periods, or health conditions in your queries for better results.",
  "support.tip2.title": "Use Natural Language",
  "support.tip2.desc":
    "Ask questions as you would to a human analyst. The AI understands conversational queries.",
  "support.tip3.title": "Follow Up",
  "support.tip3.desc":
    "Ask follow-up questions to dig deeper into the data. The system remembers your conversation context.",
  "support.tip4.title": "Spelling Flexibility",
  "support.tip4.desc":
    "Don't worry about exact spelling - the system can understand typos and variations.",

  "support.cta.heading": "Ready to Explore Your Health Data?",
  "support.cta.desc":
    "Start asking questions about your Ethiopian health data and discover insights that can improve healthcare outcomes across the country.",
  "support.cta.btn": "Start Analyzing Data",
  "support.example.label": "Example:",
  "chat.workspace.title": "Assistant Workspace",
  "chat.workspace.subtitle":
    "Ask, analyze, and export evidence-backed health insights.",
  "chat.sidebar.delete_prompt": "Delete this conversation?",
  "chat.sidebar.delete_confirm": "Delete",
  "chat.sidebar.delete_cancel": "Cancel",

  // Shared
  "common.loading": "Loading...",
  "common.noRows": "No rows found.",
  "common.empty": "—",

  // Anomaly guide page
  "guide.badge": "Anomaly intelligence",
  "guide.title": "Read the signal behind every alert.",
  "guide.subtitle":
    "A practical guide to the anomaly page: what it detects, how the scores are calculated, which data powers the results, and how to investigate what you find.",
  "guide.back": "Return to anomalies",
  "guide.explore": "Explore the guide",
  "guide.stat.lenses": "detection lenses",
  "guide.stat.lenses.text": "History, recent movement, and seasonality",
  "guide.stat.gates": "default z-score gates",
  "guide.stat.gates.text": "Medium and high severity",
  "guide.stat.levels": "hierarchy levels",
  "guide.stat.levels.text": "From federal scope to local units",
  "guide.workflow.eyebrow": "The workflow",
  "guide.workflow.title": "From scope to signal",
  "guide.workflow.desc":
    "Every run follows the same transparent path, so an alert can be traced back to its scope, data, baseline, and threshold.",
  "guide.step1.title": "Set the scope",
  "guide.step1.text":
    "Choose an indicator and drill through the organizational hierarchy from region to lower-level units.",
  "guide.step2.title": "Retrieve the history",
  "guide.step2.text":
    "Monthly indicator observations are joined with indicator and organizational-unit metadata.",
  "guide.step3.title": "Score the observations",
  "guide.step3.text":
    "Historical, recent, and seasonal baselines produce a z-score when enough history exists.",
  "guide.step4.title": "Review the signal",
  "guide.step4.text":
    "Use the chart, count cards, filters, and table to investigate the periods that need attention.",
  "guide.methods.eyebrow": "Detection methods",
  "guide.methods.title": "Three ways to spot unusual behavior",
  "guide.methods.desc":
    "The same observation can be interesting for different reasons. Reviewing the method makes the alert more useful.",
  "guide.method1.title": "Historical trend",
  "guide.method1.desc":
    "Compares the current value with all earlier reported values for the same indicator and facility.",
  "guide.method1.detail":
    "At least three historical values are needed. This is useful for spotting sustained increases, decreases, or a sharp departure from a facility's long-term pattern.",
  "guide.method2.title": "Recent movement",
  "guide.method2.desc":
    "Compares a value with the most recent reported periods to reveal abrupt changes.",
  "guide.method2.detail":
    "The current calculation uses up to the previous three periods and needs at least two values. It is responsive to recent movement, but can be sensitive with a small history.",
  "guide.method3.title": "Seasonal pattern",
  "guide.method3.desc": "Compares a month with the same month in earlier years.",
  "guide.method3.detail":
    "For example, April is compared with earlier April observations. This helps separate expected seasonality from unusual reporting behavior.",
  "guide.investigate.eyebrow": "Investigate an alert",
  "guide.investigate.title": "Click the signal, then follow the evidence",
  "guide.investigate.desc":
    "The page gives you two quick ways to move from detection to action.",
  "guide.interaction1.title": "Press a red dot",
  "guide.interaction1.text":
    "A red dot marks a high-severity time-series anomaly. Select it on the chart to open the detail panel for that period and anomaly method.",
  "guide.interaction1.item1": "Facility name and hierarchy",
  "guide.interaction1.item2": "Reported value and expected value",
  "guide.interaction1.item3": "Z-score and severity",
  "guide.interaction1.item4": "The anomaly period and method",
  "guide.interaction2.title": "Review Feedback Alerts",
  "guide.interaction2.text":
    "High-severity time anomalies are also prepared in the Feedback Alerts panel so teams can review the issue and share it with a facility.",
  "guide.interaction2.item1": "High, medium, and new counts",
  "guide.interaction2.item2": "Indicator, location, period, and method",
  "guide.interaction2.item3": "Observed value versus expected value",
  "guide.interaction2.item4": "Ready for feedback review action",
  "guide.readDetail.title": "How to read the detail",
  "guide.readDetail.text":
    "Compare the observed value with the expected value, check the z-score direction and size, confirm the facility hierarchy, and note the detection method. A red dot is a review signal, not a final diagnosis.",
  "guide.score.eyebrow": "How the score works",
  "guide.score.title": "A deviation, made measurable",
  "guide.score.desc":
    "For a current value {x}, the service calculates an expected mean {mu} and population standard deviation {sigma} from the method's history.",
  "guide.score.symbol.x": "x",
  "guide.score.symbol.mu": "μ",
  "guide.score.symbol.sigma": "σ",
  "guide.score.formula": "z = (x − μ) / σ",
  "guide.score.footnote":
    "A positive score is above expectation; a negative score is below expectation. Severity uses the absolute score, so unusually low values matter too.",
  "guide.severity.title": "Severity guide",
  "guide.severity.high": "High",
  "guide.severity.medium": "Medium",
  "guide.severity.normal": "Normal",
  "guide.severity.belowThreshold": "Below threshold",
  "guide.severity.high.text": "Strong deviation. Prioritize review.",
  "guide.severity.medium.text": "Moderate deviation. Investigate the context.",
  "guide.severity.normal.text":
    "No qualifying signal, or not enough history to score.",
  "guide.data.eyebrow": "Data & context",
  "guide.data.title": "The alert is only as good as its context",
  "guide.data.monthly.title": "Monthly indicators",
  "guide.data.monthly.text":
    "Values are organized by indicator, unit, organizational unit, and `YYYYMM` period. The analysis uses numeric observations and their reported history.",
  "guide.data.hierarchy.title": "Organizational hierarchy",
  "guide.data.hierarchy.text":
    "Scope can follow federal, region, zone, woreda, PHCU, level 6, and level 7 relationships. `South west` is understood as `South West Ethiopia`.",
  "guide.checklist.title": "Investigation checklist",
  "guide.checklist.1": "Confirm the selected indicator and hierarchy.",
  "guide.checklist.2": "Compare observed and expected values.",
  "guide.checklist.3": "Check whether history is sufficient.",
  "guide.checklist.4": "Look for missing months or reporting changes.",
  "guide.checklist.5": "Validate the result with local context.",
  "guide.note.title": "Important interpretation note",
  "guide.note.text":
    "An anomaly is a prompt for investigation, not proof of an error. Missing data can reflect late reporting, and a surprising value may reflect a genuine service change. Use the selected scope, method, history points, and local knowledge together.",

  // Anomaly dashboard (both tabs)
  "dash.tab.combined": "Anomaly Detection",
  "dash.tab.newIndicators": "Unexpected Datasets Reporting",
  "dash.sidebar.explore": "Explore this page",
  "dash.sidebar.exploreTitle": "Want to know more about this page?",
  "dash.sidebar.indicator": "Indicator",
  "dash.sidebar.orgUnit": "Org Unit Hierarchy",
  "dash.sidebar.orgUnitHint":
    "Includes selected units and their lower-level facilities.",
  "dash.sidebar.selectIndicator": "Select indicator",
  "dash.run": "Run",
  "dash.form.searchIndicators": "Search indicators...",
  "dash.form.noMatches": "No matches",
  "dash.form.all": "All",
  "dash.form.ethiopianCalendar": "Ethiopian calendar",
  "dash.form.year": "Year",
  "dash.form.month": "Month",
  "dash.action.collapseSidebar": "Collapse sidebar",
  "dash.action.expandSidebar": "Expand sidebar",
  "dash.action.collapseFeedback": "Collapse feedback",
  "dash.action.expandFeedback": "Expand feedback",
  "dash.badge.cached": "Cached view",
  "dash.badge.lastRun": "Last run",
  "dash.time.justNow": "just now",
  "dash.time.minuteAgo": "{n} min ago",
  "dash.time.minutesAgo": "{n} mins ago",
  "dash.time.hourAgo": "{n} hr ago",
  "dash.time.hoursAgo": "{n} hrs ago",
  "dash.time.dayAgo": "{n} day ago",
  "dash.time.daysAgo": "{n} days ago",
  "dash.combined.title": "Across Time Anomalies",
  "dash.combined.subtitle": "Current Fiscal Year",
  "dash.combined.refreshing": "Refreshing...",
  "dash.combined.emptyNoData":
    "No time-series values found for the selected scope.",
  "dash.combined.emptyNone": "No analysis run yet",
  "dash.combined.emptyNoneDesc":
    "Press Run in the sidebar to load the facility time-series.",
  "dash.combined.scenario": "Scenario:",
  "dash.combined.severity": "Severity:",
  "dash.combined.clear": "clear",
  "dash.combined.table": "Anomaly table",
  "dash.combined.requestFailed": "Request failed",
  "dash.count.trend": "Historical Trend",
  "dash.count.rolling": "Recent Trend",
  "dash.count.seasonal": "Seasonal Trend",
  "dash.count.missing": "Missing Data Gaps",
  "dash.chart.facilitySeries": "Facility time-series",
  "dash.chart.facilitySeriesSub":
    "Observed value vs historical, rolling, and seasonal expectations",
  "dash.chart.reportedValues": "Reported values across time",
  "dash.chart.reportedValuesSub":
    "Facility and indicator reported values by Ethiopian period",
  "dash.chart.type.line": "Line",
  "dash.chart.type.bar": "Bar",
  "dash.breakdown.title":
    "Facility breakdown - Time-series anomaly at {period}",
  "dash.breakdown.top": "Top {top} of {total} facilities",
  "dash.breakdown.high": "{n} high severity",
  "dash.table.scenario": "Scenario",
  "dash.table.period": "Period",
  "dash.table.value": "Value",
  "dash.table.expected": "Expected",
  "dash.table.zscore": "Z-score",
  "dash.table.history": "History",
  "dash.table.severity": "Severity",
  "dash.table.facility": "Facility",
  "dash.table.hierarchy": "Hierarchy",
  "dash.feedback.title": "Feedback Alerts",
  "dash.feedback.subtitle":
    "Anomalies detected and prepared for facility feedback.",
  "dash.feedback.empty": "No anomaly feedback items yet.",
  "dash.feedback.action": "Ready for feedback review",
  "dash.feedback.new": "new",
  "dash.feedback.type.time": "Time trend",
  "dash.feedback.title.text": "{indicator} at {path}",
  "dash.feedback.detail":
    "{type} in {period} reported {value} vs expected {expected} (z={score}).",
  "dash.feedback.mail.subject": "Feedback Review: {title}",
  "dash.feedback.mail.type": "Type: {type}",
  "dash.feedback.mail.severity": "Severity: {severity}",
  "dash.new.title": "Unexpected Dataset Reporting",
  "dash.new.period": "Period",
  "dash.new.orgUnits": "Org units",
  "dash.new.optionalScope": "optional scope",
  "dash.new.searchAddOrgUnits": "Search and add org units",
  "dash.new.removeOrgUnit": "Remove {name}",
  "dash.new.historyMonths": "History months",
  "dash.new.summaryOrgUnits": "Org units",
  "dash.new.summaryDatasets": "Datasets",
  "dash.new.summaryRows": "Registration rows",
  "dash.new.emptyNone": "No analysis run yet",
  "dash.new.emptyNoneDesc":
    "Press Run to load unexpected dataset reporting for {year}-{month}.",
  "dash.new.emptyClear":
    "No unexpected datasets reported for {year}-{month}",
  "dash.new.emptyClearDesc": "The selected period looks clear.",
  "dash.new.filterOrgUnit": "Filter org unit",
  "dash.new.filterOrgUnitPlaceholder": "Type org unit name",
  "dash.new.filterDataset": "Filter dataset",
  "dash.new.filterDatasetPlaceholder": "Type dataset name",
  "dash.new.clearFilters": "Clear filters",
  "dash.new.showing": "Showing {filtered} of {total}",
  "dash.new.dataTable": "Data table",
  "dash.new.th.orgUnit": "Org unit",
  "dash.new.th.dataset": "Dataset",
  "dash.new.th.firstPeriod": "First period",
  "dash.new.th.lastPeriod": "Last period",
  "dash.new.th.timely": "Timely",
  "dash.new.th.status": "Status",
  "dash.new.statusNew": "New",
  "dash.new.unknownDataset": "Unknown dataset",
  "dash.new.chart.aria": "Unexpected dataset reporting rows by dataset",
  "dash.new.chart.rows": "{dataset}: {value} registration rows",
  "dash.type.anomaly": "Anomaly",
  "dash.type.missingData": "Missing data",
  "dash.type.trend": "Trend",
  "dash.type.rolling": "Rolling",
  "dash.type.seasonal": "Seasonal",
  "dash.severity.high": "High",
  "dash.severity.medium": "Medium",
  "dash.severity.low": "Low",
  "dash.severity.new": "New",
  "dash.severity.newReporting": "New",

  // Support page - hero, navigation and chat sections
  "support.hero.badge": "Support centre",
  "support.hero.title": "How can we help?",
  "support.hero.desc":
    "Find practical guidance for asking data questions, reviewing anomalies, and generating reports.",
  "support.hero.openChat": "Open chat",
  "support.hero.browse": "Browse example questions",
  "support.quick.ask.title": "Ask the data",
  "support.quick.ask.text":
    "Use natural language to explore indicators, places, and time periods.",
  "support.quick.anomaly.title": "Review anomalies",
  "support.quick.anomaly.text":
    "Understand reporting patterns and investigate exceptions.",
  "support.quick.report.title": "Build reports",
  "support.quick.report.text":
    "Generate and export structured health performance reports.",
  "support.nav.onThisPage": "On this page",
  "support.nav.gettingStarted": "Getting started",
  "support.nav.chat": "Using chat",
  "support.nav.examples": "Example queries",
  "support.nav.tips": "Tips",
  "support.chat.title": "Chat Interface",
  "support.chat.what.title": "What is Chat?",
  "support.chat.what.desc":
    "The Chat interface lets you query health data using natural language. Ask questions about indicators, regions, time periods, and facilities — the AI agent will generate SQL queries and return results with visualizations.",
  "support.chat.step1.label": "Type your question",
  "support.chat.step1":
    "— in the input box at the bottom and press Enter or click Send.",
  "support.chat.step2.label": "View results",
  "support.chat.step2":
    "responses include charts (bar, line, pie, donut), data tables, and KPI cards.",
  "support.chat.step3.label": "Chat history",
  "support.chat.step3":
    "previous conversations appear in the sidebar. Click any to load it.",
  "support.chat.tips.title": "Tips for Better Results",
  "support.chat.tip1.title": "Be specific",
  "support.chat.tip1.text":
    "Instead of \"Show malaria data\", try \"What is the malaria positivity rate in Oromia for 2017 E.C.?\"",
  "support.chat.tip2.title": "Mention the indicator",
  "support.chat.tip2.text":
    "Include the indicator name, region, and time period in your query for the most accurate results.",
  "support.chat.tip3.title": "Ask for comparisons",
  "support.chat.tip3.text":
    "Try \"Compare malaria cases across regions in 2017 E.C.\" or \"Which woredas had the highest ANC coverage?\"",
  "support.chat.tip4.title": "Switch chart types",
  "support.chat.tip4.text":
    "After getting a response, use the chart type buttons (Bar, Line, Pie, Donut) to change the visualization.",

  // Support page - anomaly detection section
  "support.anomaly.title": "Anomaly Detection",
  "support.anomaly.sidebar.title": "Sidebar Controls",
  "support.anomaly.sidebar.indicator.label": "Indicator",
  "support.anomaly.sidebar.indicator":
    "— select the health indicator to analyze.",
  "support.anomaly.sidebar.hierarchy.label": "Org Unit Hierarchy",
  "support.anomaly.sidebar.hierarchy":
    "— drill down through Region → Zone → Woreda → PHCU → Level 6 → Level 7 to focus on a specific area.",
  "support.anomaly.sidebar.run.label": "Run Analysis",
  "support.anomaly.sidebar.run":
    "— click to fetch anomalies for the selected scope.",
  "support.anomaly.severity.title": "Understanding Anomalies",
  "support.anomaly.severity.high.label": "High severity",
  "support.anomaly.severity.high":
    "— strong deviation from expected values. Needs immediate attention.",
  "support.anomaly.severity.medium.label": "Medium severity",
  "support.anomaly.severity.medium":
    "— moderate deviation. Worth investigating.",
  "support.anomaly.severity.low.label": "Low severity",
  "support.anomaly.severity.low":
    "— minor deviation. May be expected variation.",
  "support.anomaly.types.title": "Anomaly Types",
  "support.anomaly.types.trend.label": "Trend",
  "support.anomaly.types.trend":
    "— detects gradual increases or decreases over time.",
  "support.anomaly.types.rolling.label": "Rolling",
  "support.anomaly.types.rolling":
    "— detects sudden changes compared to recent averages.",
  "support.anomaly.types.seasonal.label": "Seasonal",
  "support.anomaly.types.seasonal":
    "— detects deviations from seasonal patterns.",
  "support.anomaly.types.missing.label": "Missing Data",
  "support.anomaly.types.missing": "— flags periods with no reported data.",
  "support.anomaly.chart.title": "Reading the Chart",
  "support.anomaly.chart.intro": "The time-series chart shows:",
  "support.anomaly.chart.observed.label": "Observed value",
  "support.anomaly.chart.observed": "— the actual reported value.",
  "support.anomaly.chart.historical.label": "Historical expected",
  "support.anomaly.chart.historical": "— based on past trends.",
  "support.anomaly.chart.rolling.label": "Rolling expected",
  "support.anomaly.chart.rolling": "— based on recent averages.",
  "support.anomaly.chart.seasonal.label": "Seasonal expected",
  "support.anomaly.chart.seasonal": "— based on seasonal patterns.",
  "support.anomaly.breakdown.title": "Facility Breakdown",
  "support.anomaly.breakdown.p1":
    "Click any colored dot (anomaly marker) on the time-series chart to see a facility breakdown for that period.",
  "support.anomaly.breakdown.p2":
    "The breakdown shows all facilities under the selected org unit hierarchy that contributed to the anomaly, sorted by z-score magnitude.",
  "support.anomaly.filtering.title": "Filtering & Export",
  "support.anomaly.filtering.scenario.label": "Scenario filter",
  "support.anomaly.filtering.scenario":
    "— toggle anomaly types (Trend, Rolling, Seasonal, etc.) on/off.",
  "support.anomaly.filtering.severity.label": "Severity filter",
  "support.anomaly.filtering.severity":
    "— show only High, Medium, or Low severity items.",
  "support.anomaly.filtering.table.label": "Table toggle",
  "support.anomaly.filtering.table":
    "— expand/collapse the anomaly table below the chart.",

  // Support page - report generator section
  "support.reports.title": "Report Generator",
  "support.reports.template.title": "Sidebar — Template Selection",
  "support.reports.template.yearly.label": "MoH Yearly",
  "support.reports.template.yearly.text":
    "— annual health performance report template.",
  "support.reports.template.quarterly.label": "MoH Quarterly",
  "support.reports.template.quarterly.text": "— quarterly report template.",
  "support.reports.template.monthly.label": "MoH Monthly",
  "support.reports.template.monthly.text": "— monthly report template.",
  "support.reports.params.title": "Sidebar — Parameters",
  "support.reports.params.period.label": "Period",
  "support.reports.params.period.text":
    "— select year, quarter (for quarterly), or month (for monthly).",
  "support.reports.params.geography.label": "Geography",
  "support.reports.params.geography.text":
    "— choose National or Regional scope. For regional, select the specific region.",
  "support.reports.geography.title": "Sidebar — Geography",
  "support.reports.geography.national.label": "National",
  "support.reports.geography.national.text":
    "— generates a report for the entire country.",
  "support.reports.geography.regional.label": "Regional",
  "support.reports.geography.regional.text":
    "— generates a report for a specific region (e.g., Oromia, Amhara, Tigray).",
  "support.reports.generating.title": "Generating a Report",
  "support.reports.generating.s1":
    "Select a template (Yearly, Quarterly, or Monthly) from the sidebar.",
  "support.reports.generating.s2":
    "Set the period and geography parameters.",
  "support.reports.generating.s3":
    "Type any additional instructions in the text box at the bottom (e.g., \"Focus on immunization coverage\").",
  "support.reports.generating.s4":
    "Choose language (English or Amharic) and output format (DOCX or PPTX).",
  "support.reports.generating.s5.label": "Generate",
  "support.reports.generating.s5":
    "Click the Generate button (or press Enter) to start report generation.",
  "support.reports.improving.title": "Improving Sections",
  "support.reports.improving.intro":
    "After a report is generated, you can improve individual sections:",
  "support.reports.improving.improve.label": "Improve",
  "support.reports.improving.improve.text":
    "— hover over a section and click \"Improve\" to give AI instructions on how to rewrite it.",
  "support.reports.improving.regenerate.label": "Regenerate",
  "support.reports.improving.regenerate.text":
    "— hover over a section and click \"Regenerate\" to get a fresh version.",
  "support.reports.exporting.title": "Exporting Reports",
  "support.reports.exporting.docx.label": "DOCX",
  "support.reports.exporting.docx":
    "— exports as a Word document with charts embedded as images.",
  "support.reports.exporting.pptx.label": "PPTX",
  "support.reports.exporting.pptx":
    "— exports as a PowerPoint presentation with slides for each section.",
  "support.reports.exporting.download.label": "Download",
  "support.reports.exporting.download":
    "Click the Download button in the sidebar or header to export.",

  // Report Guide Page
  "reportGuide.badge": "Report studio",
  "reportGuide.title": "Turn health data into a report people can use.",
  "reportGuide.subtitle": "Learn how the Report Generator combines templates, live data, AI-written analysis, charts, refinement, and export into one reporting workflow.",
  "reportGuide.btn.back": "Return to report generator",
  "reportGuide.btn.workflow": "See how it works",
  "reportGuide.stats.reportRhythms.label": "report rhythms",
  "reportGuide.stats.reportRhythms.text": "Annual, quarterly, monthly",
  "reportGuide.stats.officeFormats.label": "office formats",
  "reportGuide.stats.officeFormats.text": "DOCX and PPTX",
  "reportGuide.stats.languages.label": "languages",
  "reportGuide.stats.languages.text": "English and Amharic",
  "reportGuide.workflow.eyebrow": "The workflow",
  "reportGuide.workflow.title": "From reporting idea to finished document",
  "reportGuide.workflow.desc": "The workspace keeps the structure, evidence, narrative, and presentation steps connected.",
  "reportGuide.workflow.step1.title": "Choose the shape",
  "reportGuide.workflow.step1.text": "Select an annual, quarterly, or monthly report template.",
  "reportGuide.workflow.step2.title": "Set the scope",
  "reportGuide.workflow.step2.text": "Choose the reporting period and national or regional geography.",
  "reportGuide.workflow.step3.title": "Generate with data",
  "reportGuide.workflow.step3.text": "The system queries live health data and writes grounded section summaries.",
  "reportGuide.workflow.step4.title": "Review and share",
  "reportGuide.workflow.step4.text": "Improve sections, reopen recent work, and export a polished document.",
  "reportGuide.whyWorks.eyebrow": "Why it works",
  "reportGuide.whyWorks.title": "A report workspace with evidence underneath",
  "reportGuide.whyWorks.feature1.title": "Database-grounded",
  "reportGuide.whyWorks.feature1.text": "Each section is built from live query results. Empty results are skipped instead of being filled with invented values.",
  "reportGuide.whyWorks.feature2.title": "Visual by default",
  "reportGuide.whyWorks.feature2.text": "The generator turns useful numeric results into bar, line, or pie charts and keeps the supporting table available.",
  "reportGuide.whyWorks.feature3.title": "Refine one section",
  "reportGuide.whyWorks.feature3.text": "Give a natural-language instruction to improve a section while preserving its original scope and factual grounding.",
  "reportGuide.buildBrief.eyebrow": "Build the brief",
  "reportGuide.buildBrief.title": "Give the AI a useful direction",
  "reportGuide.buildBrief.desc": "Mention the indicators, geography, period, and emphasis you care about. The selected template supplies the report structure while your instruction guides the analysis.",
  "reportGuide.buildBrief.example": "“Generate a concise maternal and child health performance report for Oromia in 2018 E.C., highlighting coverage changes and regional priorities.”",
  "reportGuide.buildBrief.controls.title": "Available controls",
  "reportGuide.buildBrief.controls.1": "Annual, quarterly, or monthly template",
  "reportGuide.buildBrief.controls.2": "Ethiopian year, quarter, and month",
  "reportGuide.buildBrief.controls.3": "National or regional geography",
  "reportGuide.buildBrief.controls.4": "English or Amharic output",
  "reportGuide.buildBrief.controls.5": "DOCX or PPTX export",
  "reportGuide.makeSharper.eyebrow": "Make it sharper",
  "reportGuide.makeSharper.title": "Improve a single section",
  "reportGuide.makeSharper.desc": "Use the section improvement panel when a paragraph needs a clearer emphasis, more concise language, or a different chart. The request includes the current content and supporting table rows, and only that section is changed.",
  "reportGuide.makeSharper.goodInstructions.title": "Good improvement instructions",
  "reportGuide.makeSharper.goodInstructions.1": "Make this more concise for an executive audience.",
  "reportGuide.makeSharper.goodInstructions.2": "Emphasize differences between regions.",
  "reportGuide.makeSharper.goodInstructions.3": "Use a short, professional summary.",
  "reportGuide.makeSharper.goodInstructions.4": "Change the chart to show the yearly trend.",
  "reportGuide.export.docx.title": "DOCX for editing",
  "reportGuide.export.docx.desc": "Creates an editable office document with title, period, geography, numbered sections, narrative, and rendered charts.",
  "reportGuide.export.pptx.title": "PPTX for presenting",
  "reportGuide.export.pptx.desc": "Creates a wide presentation with concise slide-ready text, charts, metadata, consistent styling, footers, and page numbers.",
  "reportGuide.beforeShare.title": "Before you share",
  "reportGuide.beforeShare.desc": "Check the title, period, geography, section coverage, table values, chart labels, language, and final exported document. Generated content is data-grounded, but human review remains essential for official reporting.",
} as const;

const am: Record<keyof typeof en, string> = {
  // Header
  "btn.chat": "ውይይት",
  "header.title": "የጤና መረጃ ማሳያ",
  "header.subtitle": "ስለ ህክምና መረጃዎ ጥያቄዎችን ይጠይቁ እና ግንዛቤ ያግኙ",
  "btn.help": "እርዳታ",
  "btn.newChat": "አዲስ ውይይት",
  "chat.sidebar.chats": "ውይይቶች",
  "chat.sidebar.empty": "ገና ምንም ውይይት የለም",
  "chat.clarification.choose": "አንዱን ይምረጡ",
  "btn.logout": "ውጣ",
  "btn.dash": "አኖማሊ",
  "btn.reports": "ሪፖርት ማመንጫ",

  // Report Generator
  "reports.title": "ሪፖርት ማመንጫ",
  "reports.subtitle": "የጤና መረጃ ሪፖርቶችን ያመነጩ እና ወደ ውጭ ይላኩ",
  "reports.form.title": "የሪፖርት ዝርዝሮች",
  "reports.form.reportName": "የሪፖርት ስም",
  "reports.form.reportType": "የሪፖርት ዓይነት",
  "reports.form.startDate": "የመጀመሪያ ቀን",
  "reports.form.endDate": "የመጨረሻ ቀን",
  "reports.form.language": "ቋንቋ",
  "reports.form.format": "የፋይል ቅርጸት",
  "reports.language.en": "እንግሊዝኛ",
  "reports.language.am": "አማርኛ",
  "header.lang.en": "እን",
  "header.lang.am": "አማ",
  "reports.format.docx": "DOCX",
  "reports.format.pptx": "PPTX",
  "reports.download.docx": "DOCX አውርድ",
  "reports.download.pptx": "PPTX አውርድ",
  "reports.sidebar.templateSelection": "የአብነት ምርጫ",
  "reports.sidebar.parameters": "መለኪያዎች",
  "reports.sidebar.geography": "አካባቢ",
  "reports.sidebar.aiGuidance": "የAI መመሪያ",
  "reports.sidebar.month": "ወር",
  "reports.sidebar.quarter": "ሩብ",
  "reports.sidebar.year": "ዓመት",
  "reports.sidebar.region": "ክልል",
  "reports.sidebar.national": "ብሔራዊ",
  "reports.sidebar.regional": "ክልላዊ",
  "reports.sidebar.nationalLevel": "ብሔራዊ ደረጃ",
  "reports.preview.readyTitle": "ሪፖርት ለመመንጨት ዝግጁ",
  "reports.preview.readyDesc":
    "በግራ ያሉትን መለኪያዎች ያስተካክሉ እና \"ሪፖርት አመንጭ\" በመጫን ማሳያውን ይመልከቱ።",
  "reports.recent.title": "በቅርብ የተፈጠሩ",
  "reports.recent.empty": "ከሶስት የቅርብ ሪፖርቶችዎ በኋላ እዚህ ይታያሉ።",
  "reports.recent.sections": "ክፍሎች",
  "reports.recent.open": "ክፈት",
  "reports.form.generate": "ሪፖርት አመንጭ",
  "reports.errors.periodTitle": "የተጠየቀው የሪፖርት ጊዜ አይገኝም",
  "reports.errors.geographyTitle": "የተጠየቀው የሪፖርት አካባቢ አይገኝም",
  "reports.errors.generationTitle": "የሪፖርት ማስነድ አልተሳካም",
  "reports.errors.conflictTitle": "የተገነበለው መረጃ ከጥያቄው ጋር ይገዛረናል",
  "reports.errors.generationFailed": "ሪፖርቱ ሊመገበር አልተቻለም።",
  "reports.errors.headerUnavailable": "የሪፖርት ጊዜ አይገኝም",
  "reports.errors.periodUnavailable": "ጊዜ አይገኝም",
  "reports.errors.geographyUnavailable": "አካባቢ አይገኝም",
  "reports.type.malaria": "የወባ አዝማሚያዎች",
  "reports.type.hiv": "የHIV ስታቲስቲክስ",
  "reports.type.tb": "የቲቢ ማጠቃለያ",
  "reports.type.regional": "የክልል ንጽጽር",
  "btn.stop": "አቁም",

  // Welcome
  "welcome.greeting.morning": "እንደምን አደሩ",
  "welcome.greeting.afternoon": "እንደምን ዋሉ",
  "welcome.greeting.evening": "እንደምን አመሹ",
  "welcome.heading": "ወደ ጤና መረጃ ማሳያ ወኪል እንኳን በደህና መጡ",
  "welcome.description":
    "ብልህ የጤና ግንዛቤ አጋርዎ። ስለ በሽታ አዝማሚያዎች፣ የክልል ጤና ስታቲስቲክስ፣ የታካሚ ስነ-ሕዝብ፣ ወይም የጤና ውጤቶችን ለማሻሻል የሚያስፈልጉ ማንኛውንም ግንዛቤ ይጠይቁ።",

  // Feature highlights
  "feature.analytics.title": "ቅጽበታዊ ትንተና (Real-time Analytics)",
  "feature.analytics.desc": "ከህክምና መረጃዎ ፈጣን ግንዛቤ ያግኙ",
  "feature.viz.title": "ብልህ ማሳያዎች (Smart Visualizations)",
  "feature.viz.desc": "ራስ-ሰር ቻርቶች እና ግራፎች",
  "feature.patient.title": "የታካሚ ግንዛቤዎች (Patient Insights)",
  "feature.patient.desc": "የታካሚ ስነ-ሕዝብ እና የጤና አዝማሚያዎችን በተሻለ ይረዱ",

  // Suggestion queries
  "suggestions.heading": "እነዚህን ታዋቂ ጥያቄዎች ይሞክሩ",
  "suggestion.1.category": "ክትባት",
  "suggestion.1.text": "በክልል ሙሉ የክትባት ሽፋን ለቅርብ ዓመት።",
  "suggestion.2.category": "የእናት ጤና",
  "suggestion.2.text": "በክልል የባለሙያ ወሊድ እገዛ ሬት።",
  "suggestion.3.category": "ANC",
  "suggestion.3.text": "ANC 4+ ጉዳዮች ያላቸው ሴቶች ቁጥር በክልል።",

  // Loading
  "loading.title": "ጥያቄዎን በመተንተን ላይ...",
  "loading.subtitle": "ይህ ጥቂት ጊዜ ሊወስድ ይችላል",

  // Chat input
  "input.placeholder":
    "ስለ ክትባት፣ የእናት ጤና ወይም ANC ጠቋሚዎች ይጠይቁ...",
  "input.suggestion.1": "በክልል ሙሉ የክትባት ሽፋን",
  "input.suggestion.2": "የፔንታ 3 እና የሚስልስ የመጀመሪያ መድሃኒት ሽፋን አዝማሚያ",
  "input.suggestion.3": "በክልል የANC የመጀመሪያ ጉብኝት ሽፋን",
  "input.suggestion.4": "ANC 4+ ጉዳዮች በተቋም",
  "input.suggestion.5": "በብሔራዊ ደረጃ የእናት ጤና ጠቋሚዎች",
  "input.suggestion.6": "በክልሎች የክትባት ሽፋን ንጽጽር",
  "input.suggestion.7": "በክልል የወባ ጉዳዮች ለቅርብ ዓመት",
  "input.suggestion.8": "የHIV ሪፖርቲንግ አዝማሚያ በየ12 ወሩ",
  "input.suggestion.9": "በክልል የቲቢ ሕክምና እርካታ ደረጃ",
  "input.disclaimer": "በAI የተመነጩ ምላሾች ስህተት ሊኖራቸው ይችላል። ውሳኔ ከመስጠትዎ በፊት ውሂቡንና ምንጮቹን ያረጋግጡ።",

  // Starter prompts (empty state)
  "starters.heading": "በጥያቄ ይጀምሩ",
  "starters.scope":
    "በክልል፣ በተቋም እና በበጀት ዓመት ደረጃ የኢትዮጵያ የወባ፣ HIV፣ ቲቢ፣ ክትባትና የእናት ጤና ጠቋሚዎችን ያካትታል።",
  "starters.theme.immunization": "የክትባት ሽፋን",
  "starters.theme.maternal": "የእናት ጤናና ANC",
  "starters.theme.diseases": "ወባ፣ HIV እና ቲቢ",

  // Table / Chart labels
  "table.title": "የጥያቄ ውጤቶች",
  "table.results": "ውጤቶች",

  // Support page
  "support.title": "ጤና መረጃ ማሳያ - የተጠቃሚ መመሪያ",
  "support.subtitle": "ከጤና መረጃ ትንተናዎ ከፍተኛውን እንዴት ማግኘት እንደሚቻል ይማሩ",
  "support.backToChat": "ወደ ውይይት ተመለስ",

  "support.gettingStarted": "መጀመር",
  "support.howToUse": "ስርዓቱን እንዴት መጠቀም እንደሚቻል",
  "support.step1.title": "ጥያቄዎን ይጠይቁ",
  "support.step1.desc":
    "የጤና መረጃ ጥያቄዎን በተፈጥሯዊ ቋንቋ በውይይት ሳጥኑ ውስጥ ይጻፉ።",
  "support.step2.title": "AI ትንተና ያግኙ",
  "support.step2.desc": "ስርዓቱ ጥያቄዎን ይተነትናል እና ተገቢ ማሳያዎችን ያዘጋጃል።",
  "support.step3.title": "ውጤቶችን ይገልግሉ",
  "support.step3.desc":
    "ቻርቶችን፣ ሰንጠረዦችን እና AI ማብራሪያዎችን ይመልከቱ። ለጠለቅ ያሉ ግንዛቤዎች ተጨማሪ ጥያቄዎችን ይጠይቁ።",

  "support.context.title": "የኢትዮጵያ ጤና መረጃ ዐውድ",
  "support.context.intro":
    "ይህ ስርዓት ከኢትዮጵያ የጤና ተቋማት የጤና መረጃ ይዟል፣ የሚሸፍነው:",
  "support.context.diseases":
    "በሽታዎች: HIV/AIDS፣ ሳንባ ነቀርሳ፣ ወባ እና ሌሎች ሁኔታዎች",
  "support.context.regions":
    "ክልሎች: አማራ፣ ኦሮሚያ፣ ትግራይ፣ አዲስ አበባ እና ሁሉም የኢትዮጵያ ክልሎች",
  "support.context.facilities":
    "ተቋማት: ሆስፒታሎች፣ ጤና ጣቢያዎች፣ ጤና ኬላዎች እና ክሊኒኮች",
  "support.context.indicators":
    "ጠቋሚዎች (Indicators): የህክምና ሽፋን (Treatment Coverage)፣ የጉዳይ ማስታወቂያዎች (Case Notifications)፣ ፖዚቲቪቲ ሬቶች (Positivity Rates)",

  "support.vizTypes": "የማሳያ ዓይነቶች",
  "support.chart.bar": "ባር ቻርቶች",
  "support.chart.bar.desc":
    "ክልሎችን፣ ተቋማትን ወይም የበሽታ ዓይነቶችን ያነጻጽሩ",
  "support.chart.bar.example": "የወባ ጉዳዮች ቁጥር በክልል",
  "support.chart.line": "መስመር ቻርቶች",
  "support.chart.line.desc": "በጊዜ ወቅቶች ላይ አዝማሚያዎችን ያሳዩ",
  "support.chart.line.example": "በ2016 ወርሃዊ ቲቢ ጉዳዮች",
  "support.chart.pie": "ፓይ ቻርቶች",
  "support.chart.pie.desc": "ድርሻዎችን እና መቶኛዎችን ያሳዩ",
  "support.chart.pie.example": "የተቋማት ድርሻ በባለቤትነት ዓይነት",
  "support.chart.table": "ሰንጠረዦች",
  "support.chart.table.desc": "ጥሬ መረጃ ማሳያ ከማስተካከያ እና ማጣሪያ ጋር",
  "support.chart.table.example": "በኦሮሚያ ያሉ ሁሉም የጤና ተቋማት ዝርዝር",
  "support.chart.kpi": "KPI ካርዶች",
  "support.chart.kpi.desc": "ነጠላ ቁልፍ መለኪያዎች እና ድምሮች",
  "support.chart.kpi.example": "የጤና ተቋማት ጠቅላላ ቁጥር",

  "support.examples": "ምሳሌ ጥያቄዎች",

  "support.cat.regional": "የክልል ትንተና",
  "support.cat.regional.q1": "በአማራ ክልል ያሉ ሁሉንም የጤና ተቋማት አሳይ",
  "support.cat.regional.q2": "የወባ ጉዳዮች ቁጥር በክልል",
  "support.cat.regional.q3": "የቲቢ ጠቋሚዎችን በሁሉም ክልሎች ተነጻጽር",
  "support.cat.regional.q4": "የትኛው ክልል ከፍተኛ የጤና ጣቢያዎች አሉት?",

  "support.cat.time": "ጊዜ-ተኮር ትንተና",
  "support.cat.time.q1": "በ2016 ወርሃዊ የHIV ጉዳዮች",
  "support.cat.time.q2": "ባለፈው ዓመት ውስጥ የወባ ፖዚቲቪቲ ሬት አዝማሚያ",
  "support.cat.time.q3": "ሩብ-ከ-ሩብ ያለ ንጽጽር የቲቢ ጉዳዮች",
  "support.cat.time.q4": "የጤና ጠቋሚዎች በዓመት አሳይ",

  "support.cat.indicators": "የጤና ጠቋሚዎች",
  "support.cat.indicators.q1": "ሁሉንም ያሉ የጤና ጠቋሚዎች ዘርዝር",
  "support.cat.indicators.q2": "የHIV ህክምና ሽፋን በተቋም",
  "support.cat.indicators.q3": "በኦሮሚያ ያለ የወባ ፖዚቲቪቲ ሬቶች",
  "support.cat.indicators.q4": "የቲቢ ማስታወቂያ ሬቶች በዞን",

  "support.tips": "ለተሻለ ውጤት ምክሮች",
  "support.tip1.title": "ልዩ ይሁኑ",
  "support.tip1.desc":
    "ለተሻለ ውጤት በጥያቄዎች ውስጥ ልዩ ክልሎችን፣ ጊዜ ወቅቶችን ወይም የጤና ሁኔታዎችን ያካትቱ።",
  "support.tip2.title": "ተፈጥሯዊ ቋንቋ ይጠቀሙ",
  "support.tip2.desc":
    "ለሰው ተንታኝ እንደሚጠይቁ ጥያቄ ይጠይቁ። AI የውይይት ጥያቄዎችን ይረዳል።",
  "support.tip3.title": "ተጨማሪ ይጠይቁ",
  "support.tip3.desc":
    "በመረጃው ውስጥ ጠለቅ ብለው ለመመርመር ተጨማሪ ጥያቄዎችን ይጠይቁ። ስርዓቱ የውይይት ዐውድዎን ያስታውሳል።",
  "support.tip4.title": "የፊደል ተጣጣሚነት",
  "support.tip4.desc":
    "ስለ ትክክለኛ ፊደል አይጨነቁ - ስርዓቱ ስህተቶችን እና ልዩነቶችን ይረዳል።",

  "support.cta.heading": "የጤና መረጃዎን ለመዳሰስ ዝግጁ ነዎት?",
  "support.cta.desc":
    "ስለ ኢትዮጵያ የጤና መረጃ ጥያቄዎችን መጠየቅ ይጀምሩ እና በመላ አገሪቱ የጤና ውጤቶችን ሊያሻሽሉ የሚችሉ ግንዛቤዎችን ያግኙ።",
  "support.cta.btn": "መረጃ መተንተን ይጀምሩ",
  "support.example.label": "ምሳሌ:",
  "chat.workspace.title": "የረዳት የስራ ቦታ",
  "chat.workspace.subtitle": "ጠይቅ፣ ተንትን፣ እና በማስረጃ የተደገፉ የጤና ግንዛቤዎችን አውጣ።",
  "chat.sidebar.delete_prompt": "ይህን ውይይት ማጥፋት ይፈልጋሉ?",
  "chat.sidebar.delete_confirm": "ሰርዝ",
  "chat.sidebar.delete_cancel": "አቁም",

  // Shared
  "common.loading": "በመጫን ላይ...",
  "common.noRows": "ምንም ክፍል አልተገኘም።",
  "common.empty": "—",

  // Anomaly guide page
  "guide.badge": "የአኖማሊ መረጃ እውቀት",
  "guide.title": "እያንዳንዱ ማስጠንቀቂያ የሚገኝውን ምልክት ይምረጡ።",
  "guide.subtitle":
    "የአኖማሊ ገጹ ተግባራዊ መመሪያ፦ ምንነትን ይመለከታል፣ ውጤቶች እንዴት ይሰላሉ፣ ውጤቶቹን የሚያስቀምጡ መረጃዎች ምንናው፣ እና ያገኙትን እንዴት መመርመር ነው።",
  "guide.back": "ወደ አኖማሊዎች ተመለስ",
  "guide.explore": "መመሪያውን ይመልከቱ",
  "guide.stat.lenses": "የመለያ ስላንስ",
  "guide.stat.lenses.text": "ታሪክ፣ የቅርብ አንቀሳቀስና የወቅት አዘጋጅነት",
  "guide.stat.gates": "መሠረታዊ z-score ጫትዎች",
  "guide.stat.gates.text": "መካከለኛና ከፍተኛ አሰራር",
  "guide.stat.levels": "የአዋራድ ደረጃዎች",
  "guide.stat.levels.text": "ከፌዴራላዊ ወሰን እስከ የአካባቢ ተቋማት",
  "guide.workflow.eyebrow": "የስራ ሂደት",
  "guide.workflow.title": "ከወሰን እስከ ምልክት",
  "guide.workflow.desc":
    "እየእያንዳንዱ ስራ ተመሳሳይ ግልጽ መንገድ ይከተላል፤ ስለዚህ ማስጠንቀቂያውን ወደ ወሰኑ፣ መረጃው፣ መሠረቱና ገደቡ መከታተል ይቻላል።",
  "guide.step1.title": "ወሰኑን ያስቀምጡ",
  "guide.step1.text":
    "አንድ ጠቋሚ ይምረጡና ከክልል ጀምሮ እስከ ዝቅተኛ ደረጃ ተቋማት ድረስ በድርሻ አዋራድ ወሰን ይሁኑ።",
  "guide.step2.title": "ታሪኩን ያስገኙ",
  "guide.step2.text":
    "የወር የጠቋሚ ምልከታዎች ከጠቋሚ እና ከድርሻ አዋራድ ምርጃ ጋር ይዋሃዳሉ።",
  "guide.step3.title": "ምልከታዎቹን ዳትጠሱ",
  "guide.step3.text":
    "በቂ ታሪክ ሲኖር የታሪክ፣ የቅርብና የወቅት መሠረቶች z-score ይሰጣሉ።",
  "guide.step4.title": "ምልክቱን ይመልከቱ",
  "guide.step4.text":
    "በግሉ አንዳንድ ምርጫዊ ጊዜዎችን ለመመርመር ቻርት፣ የቆጠራ ካርዶች፣ ማጣሪያዎችና ሰንጠረድ ይጠቀሙ።",
  "guide.methods.eyebrow": "የመለያ ዘዴዎች",
  "guide.methods.title": "ያልተለመዱ ባህሪዎችን ለመለየት ሦስት መንገዶች",
  "guide.methods.desc":
    "እሁኑ ምልከታ በተየረየ ምክንያቶች ስለሆነ ተፈጥሞ ክፍተት ሊሰጥ ይችላል። ዘዴውን መገምገም ማስጠንቀቂያውን የበለጠ ጠቅሚ ያደርገዋል።",
  "guide.method1.title": "የታሪክ አዘጋጅነት",
  "guide.method1.desc":
    "የአሁኑን ዋጋ ከተመሳሳዩ ጠቋሚ እና ተቋም ጋር ያለው ከበፊት የተመዘገቡ ዋጋዎች ጋር ያነጻጽራል።",
  "guide.method1.detail":
    "ቢያንስ ሦስት የታሪክ ዋጋዎች ያስፈልጋሉ። ያላመለወጥ መጨመር፣ ቀንሶ መቀነስ ወይም ከተቋሙ የረዘመ አማራይ ላይ የሆነ አስተያየት ለመለየት ጠቅሚ ነው።",
  "guide.method2.title": "የቅርብ አንቀሳቀስ",
  "guide.method2.desc":
    "አንድ ዋጋ ከቅርብ ጊዜዎች ጋር በማነጻጸር ድንጋጤ ለውጦችን ያሳያል።",
  "guide.method2.detail":
    "አሁኑ ሰሌዳው እስከ የቀዳሚው ሦስት ጊዜዎች ድረስ ይጠቀማል፤ ከሁለት ቅድሚያ ዋጋ ይፈልጋል። የቅርብ አንቀሳቀስን ይመለከታል፣ ግን ከትንሽ ታሪክ ጋር ሰሜታዊ ሊሆን ይችላል።",
  "guide.method3.title": "የወቅት አዘጋጅነት",
  "guide.method3.desc": "አንድ ወር ከቀዳሚው ጊዜ ተመሳሳዩ ወር ጋር ያነጻጽራል።",
  "guide.method3.detail":
    "ለምሳሌ፣ ኤፕሪል ከቀዳሚው ኤፕሪል ምልከታዎች ጋር ይነጻጸራል። ይህ የሚጠበቁትን የወቅት አዘጋጅነት ከያልተለመደ የሪፖርት ባህሪ ይለያል።",
  "guide.investigate.eyebrow": "ማስጠንቀቂያ ይመርምሩ",
  "guide.investigate.title": "ምልክቱን ይጫኑ፣ ከዚያ ማስረጃውን ይከተሉ",
  "guide.investigate.desc":
    "ገጹ ከመለየት ወደ እርምጃ የሚሄዱበት ሁለት ፈጣን መንገዶችን ይሰጣል።",
  "guide.interaction1.title": "ቀይ ነጥብን ይጫኑ",
  "guide.interaction1.text":
    "ቀይ ነጥብ ከፍተኛ አሰራር ያለው የጊዜ አቋራጭ አኖማሊ ያሳያል። በቻርቱ ላይ ሲምረጡ ለዚህ ጊዜና የአኖማሊ ዘዴ የዝርዝር ክፍል ይከፈታል።",
  "guide.interaction1.item1": "የተቋሙ ስምና አዋራድ",
  "guide.interaction1.item2": "የተመዘገበና የተጠበቀ ዋጋ",
  "guide.interaction1.item3": "z-score እና አሰራር",
  "guide.interaction1.item4": "የአኖማሊ ጊዜውና ዘዴው",
  "guide.interaction2.title": "የግብረ መልስ ማስጠንቀቂያዎችን ይመልከቱ",
  "guide.interaction2.text":
    "ከፍተኛ አሰራር ያላቸው የጊዜ አኖማሊዎችም በግብረ መልስ ማስጠንቀቂያዎች ክፍል ውስጥ ተዘጋጅተዋል፤ ቡድኖች ችግሩን ለመመልከትና ከተቋሙ ጋር ለማጋራት ያስችላሉ።",
  "guide.interaction2.item1": "የከፍተኛ፣ መካከለኛና አዲስ ቆጠራዎች",
  "guide.interaction2.item2": "ጠቋሚ፣ አካባቢ፣ ጊዜና ዘዴ",
  "guide.interaction2.item3": "የተመለከተ ዋጋና የተጠበቀ ዋጋ",
  "guide.interaction2.item4": "ለግብረ መልስ ግምገማ ዝግጁ እርምጃ",
  "guide.readDetail.title": "ዝርዝሩን እንዴት መቀበር",
  "guide.readDetail.text":
    "የተመለከተውን ዋጋ ከተጠበቀው ጋር ያነጻጽሩ፣ የz-score አቅጣጫና መጠን ያረጋግጡ፣ የተቋሙን አዋራድ ያረጋግጡ፣ እና የመለያ ዘዴውን ይመዝግቡ። ቀይ ነጥብ ለግምገማ የሚሆን ምልክት ነው፣ የመጨረሻ ውሳኔ አይደለም።",
  "guide.score.eyebrow": "ውጤቱ እንዴት ይሰላል",
  "guide.score.title": "ልዩነት፣ በሚሻል መሠረት",
  "guide.score.desc":
    "ለአሁኑ ዋጋ {x} አገልግሎቱ ከዘዴው ታሪክ የሚጠበቅ የመሠረት አማካኝ {mu} እና የህዝብ መደበኛ ልዩነት {sigma} በመሰላቸው ይሰላል።",
  "guide.score.symbol.x": "x",
  "guide.score.symbol.mu": "μ",
  "guide.score.symbol.sigma": "σ",
  "guide.score.formula": "z = (x − μ) / σ",
  "guide.score.footnote":
    "አዎንታዊ ውጤት ከተጠበቀው በላይ ሲሆን፣ አስተሳሚ ውጤት ከተጠበቀው ውስጥ ሲሆን ያሳያል። አሰራሩ በፍጹም ውጤቱ ላይ የተመሠረተ ሲሆን፣ በጣም ዝቅተኛ ዋጋዎችም አስፈላጊ ናቸው።",
  "guide.severity.title": "የአሰራር መመሪያ",
  "guide.severity.high": "ከፍተኛ",
  "guide.severity.medium": "መካከለኛ",
  "guide.severity.normal": "ዕሳዊ",
  "guide.severity.belowThreshold": "ከገደቡ በታች",
  "guide.severity.high.text": "ጠንካር ልዩነት። ግምገማውን በቅድሚያ ያስቀምጡ።",
  "guide.severity.medium.text": "መካከለኛ ልዩነት። አውዳውን ያረጋግጡ።",
  "guide.severity.normal.text":
    "የሚመለከት ምልክት የለም፣ ወይም ለመቁጠር በቂ ታሪክ የለም።",
  "guide.data.eyebrow": "መረጃና አውዳ",
  "guide.data.title": "ማስጠንቀቂያው በአውዳው ብቻ ጥራት አለው",
  "guide.data.monthly.title": "የወር ጠቋሚዎች",
  "guide.data.monthly.text":
    "ዋጋዎች በጠቋሚ፣ በክፍል፣ በድርሻ አዋራድ ክፍልና በ`YYYYMM` ጊዜ ይደራጃሉ። ትንተናው የቁጥር ምልከታዎችንና የተመዘገቡትን ታሪክ ይጠቀማል።",
  "guide.data.hierarchy.title": "የድርሻ አዋራድ",
  "guide.data.hierarchy.text":
    "ወሰኑ ከፌዴራላዊ፣ ክልል፣ ዞን፣ ወረዳ፣ PHCU፣ ደረጃ 6ና ደረጃ 7 መዋቅረቶችን መከተል ይችላል። `South west` እንደ `South West Ethiopia` ይታወቃል።",
  "guide.checklist.title": "የምርመራ ማረጋገጫ ዝርዝር",
  "guide.checklist.1": "የተመረጠውን ጠቋሚና አዋራድ ያረጋግጡ።",
  "guide.checklist.2": "የተመለከተና የተጠበቀ ዋጋዎችን ያነጻጽሩ።",
  "guide.checklist.3": "ታሪኩ በቂ ነው ዘንድ ያረጋግጡ።",
  "guide.checklist.4": "የጠፋቱ ወሮች ወይም የሪፖርት ለውጦችን ይፈልጉ።",
  "guide.checklist.5": "ውጤቱን በአካባቢው አውዳ ያረጋግጡ።",
  "guide.note.title": "አስፈላጊ የትርጉም ማስጠንቀቂያ",
  "guide.note.text":
    "አኖማሊ ለምርመራ ጥራት ነው፣ የስህተት ማረጋገጫ አይደለም። የጠፋተኛ መረጃ ዘግይቶ ሪፖርት መዘግየን ያሳያል፣ እና አስገርቶ የሚታይ ዋጋ እውነተኛ የአገልግሎት ለውጥ ሊሆን ይችላል። የተመረጠውን ወሰን፣ ዘዴ፣ የታሪክ ነጥቦችና የአካባቢ እውቀትን በጋራ ይጠቀሙ።",

  // Anomaly dashboard (both tabs)
  "dash.tab.combined": "አኖማሊ ለይቶ",
  "dash.tab.newIndicators": "ያልተጠበቁ የመረጃ ማጋራቻ ሪፖርት",
  "dash.sidebar.explore": "ይህን ገጹን ያስሱ",
  "dash.sidebar.exploreTitle": "ስለ ይህ ገጽ ተጨማሪ ማወቂት እኖር?",
  "dash.sidebar.indicator": "ጠቋሚ",
  "dash.sidebar.orgUnit": "የድርሻ አዋራድ አዋራድ",
  "dash.sidebar.orgUnitHint": "የተመረጡ ክፍሎችንና ዝቅተኛ ደረጃ ተቋማታቸውን ያካትታል።",
  "dash.sidebar.selectIndicator": "ጠቋሚ ይምረጡ",
  "dash.run": "አሂድ",
  "dash.form.searchIndicators": "ጠቋሚዎችን ይፈልጉ...",
  "dash.form.noMatches": "ተመሳሳይ የለም",
  "dash.form.all": "ሁሉም",
  "dash.form.ethiopianCalendar": "የኢትዮጵያ ዘመን",
  "dash.form.year": "ዓመት",
  "dash.form.month": "ወር",
  "dash.action.collapseSidebar": "ጎን ወስትክ",
  "dash.action.expandSidebar": "ጎን አስፋፋ",
  "dash.action.collapseFeedback": "ግብረ መልስ ወስትክ",
  "dash.action.expandFeedback": "ግብረ መልስ አስፋፋ",
  "dash.badge.cached": "የተቀመጠ እይታ",
  "dash.badge.lastRun": "የመጨረሻ ስራ",
  "dash.time.justNow": "አሁን",
  "dash.time.minuteAgo": "ከ{n} ደቂቃ በፊት",
  "dash.time.minutesAgo": "ከ{n} ደቂቃዎች በፊት",
  "dash.time.hourAgo": "ከ{n} ሰዓት በፊት",
  "dash.time.hoursAgo": "ከ{n} ሰዓታት በፊት",
  "dash.time.dayAgo": "ከ{n} ቀን በፊት",
  "dash.time.daysAgo": "ከ{n} ቀናት በፊት",
  "dash.combined.title": "በጊዜ ላይ አኖማሊዎች",
  "dash.combined.subtitle": "የአሁኑ የፋይስካል ዓመት",
  "dash.combined.refreshing": "በመዘምን...",
  "dash.combined.emptyNoData": "ለተመረጠው ወሰን የጊዜ አቋራጭ ዋጋዎች አልተገኙም።",
  "dash.combined.emptyNone": "እስካሁን ትንተና አልተሮገመም",
  "dash.combined.emptyNoneDesc":
    "የተቋማቸውን የጊዜ አቋራጭ ለመጫን በጎኑ ውስጥ አንድ ጊዜ ማሂድ ይጫኑ።",
  "dash.combined.scenario": "ሁኔታ፦",
  "dash.combined.severity": "አሰራር፦",
  "dash.combined.clear": "አጽዳ",
  "dash.combined.table": "የአኖማሊ ሰንጠረድ",
  "dash.combined.requestFailed": "ጥያቄው አልተሳካም",
  "dash.count.trend": "የታሪክ አዘጋጅነት",
  "dash.count.rolling": "የቅርብ አዘጋጅነት",
  "dash.count.seasonal": "የወቅት አዘጋጅነት",
  "dash.count.missing": "የጠፋተኛ መረጃ ክፍተቶች",
  "dash.chart.facilitySeries": "የተቋም በጊዜ አቋራጭ",
  "dash.chart.facilitySeriesSub":
    "የተመለከተ ዋጋ ከታሪክ፣ ከቅርብና ከወቅት ጥበቃዎች ጋር",
  "dash.chart.reportedValues": "በጊዜ ላይ የተመዘገቡ ዋጋዎች",
  "dash.chart.reportedValuesSub":
    "በኢትዮጵያ ጊዜ ሰሌዳ ላይ የተቋምና የጠቋሚ የተመዘገቡ ዋጋዎች",
  "dash.chart.type.line": "መስመር",
  "dash.chart.type.bar": "አምድ",
  "dash.breakdown.title": "የተቋም ማበረሳያ - በ{period} የጊዜ አቋራጭ አኖማሊ",
  "dash.breakdown.top": "ከ{total} ተቋማት ውስጥ ከመጀመሪያው {top}",
  "dash.breakdown.high": "{n} ከፍተኛ አሰራር",
  "dash.table.scenario": "ሁኔታ",
  "dash.table.period": "ጊዜ",
  "dash.table.value": "ዋጋ",
  "dash.table.expected": "የተጠበቀ",
  "dash.table.zscore": "Z-score",
  "dash.table.history": "ታሪክ",
  "dash.table.severity": "አሰራር",
  "dash.table.facility": "ተቋም",
  "dash.table.hierarchy": "አዋራድ",
  "dash.feedback.title": "የግብረ መልስ ማስጠንቀቂያዎች",
  "dash.feedback.subtitle": "የተለዩና ለተቋም ግብረ መልስ የተዘጋጁ አኖማሊዎች።",
  "dash.feedback.empty": "እስካሁን የአኖማሊ ግብረ መልስ ዝርዝር የለም።",
  "dash.feedback.action": "ለግብረ መልስ ግምገማ ዝግጁ",
  "dash.feedback.new": "አዲስ",
  "dash.feedback.type.time": "የጊዜ አዘጋጅነት",
  "dash.feedback.title.text": "{indicator} በ{path}",
  "dash.feedback.detail":
    "{type} በ{period} {value} ተመዝግቧል፣ {expected} ተጠብቆ ነው (z={score})።",
  "dash.feedback.mail.subject": "የግብረ መልስ ግምገማ: {title}",
  "dash.feedback.mail.type": "ዓይነት፦ {type}",
  "dash.feedback.mail.severity": "አሰራር፦ {severity}",
  "dash.new.title": "የያልተጠበቁ የመረጃ ማጋራቻ ሪፖርት",
  "dash.new.period": "ጊዜ",
  "dash.new.orgUnits": "ድርሻ ክፍሎች",
  "dash.new.optionalScope": "አማራጭ ወሰን",
  "dash.new.searchAddOrgUnits": "የድርሻ ክፍሎችን ይፈልጉና ይጨምሩ",
  "dash.new.removeOrgUnit": "{name} አስወግድ",
  "dash.new.historyMonths": "የታሪክ ወራት",
  "dash.new.summaryOrgUnits": "ድርሻ ክፍሎች",
  "dash.new.summaryDatasets": "የመረጃ ስብስቦች",
  "dash.new.summaryRows": "የመዝገብ ርዝጮች",
  "dash.new.emptyNone": "እስካሁን ትንተና አልተሮገመም",
  "dash.new.emptyNoneDesc":
    "ለ{year}-{month} ያልተጠበቁ የመረጃ ማጋራቻ ሪፖርት ለመጫን አንድ ጊዜ ማሂድ ይጫኑ።",
  "dash.new.emptyClear": "ለ{year}-{month} ያልተጠበቀ የመረጃ ስብስብ አልተመዘገበም",
  "dash.new.emptyClearDesc": "የተመረጠው ጊዜ አስጽድቶ ይመስላል።",
  "dash.new.filterOrgUnit": "የድርሻ ክፍል ኣማራጭ",
  "dash.new.filterOrgUnitPlaceholder": "የድርሻ ክፍሉን ስም ይጻፉ",
  "dash.new.filterDataset": "የመረጃ ስብስብ ኣማራጭ",
  "dash.new.filterDatasetPlaceholder": "የመረጃ ስብስቡን ስም ይጻፉ",
  "dash.new.clearFilters": "ማጣሪያዎችን አጽዳ",
  "dash.new.showing": "ከ{total} ውስጥ {filtered} ታይቷል",
  "dash.new.dataTable": "የመረጃ ሰንጠረድ",
  "dash.new.th.orgUnit": "የድርሻ ክፍል",
  "dash.new.th.dataset": "የመረጃ ስብስብ",
  "dash.new.th.firstPeriod": "የመጀመሪያ ጊዜ",
  "dash.new.th.lastPeriod": "የመጨረሻ ጊዜ",
  "dash.new.th.timely": "በጊዜው",
  "dash.new.th.status": "ሁኔታ",
  "dash.new.statusNew": "አዲስ",
  "dash.new.unknownDataset": "ያልታወቀ የመረጃ ስብስብ",
  "dash.new.chart.aria": "በመረጃ ስብስብ የተዘጋጁ የመዝገብ ርዝጮች",
  "dash.new.chart.rows": "{dataset}፦ {value} የመዝገብ ርዝጮች",
  "dash.type.anomaly": "አኖማሊ",
  "dash.type.missingData": "የጠፋተኛ መረጃ",
  "dash.type.trend": "አዘጋጅነት",
  "dash.type.rolling": "ተመንጋንዊ",
  "dash.type.seasonal": "ወቅታዊ",
  "dash.severity.high": "ከፍተኛ",
  "dash.severity.medium": "መካከለኛ",
  "dash.severity.low": "ዝቅተኛ",
  "dash.severity.new": "አዲስ",
  "dash.severity.newReporting": "አዲስ",

  // Support page - hero, navigation and chat sections
  "support.hero.badge": "የድጋፍ ማዕከል",
  "support.hero.title": "እንዴት ልክልልዎት።",
  "support.hero.desc":
    "የመረጃ ጥያቄዎችን ለመጠየቅ፣ አኖማሊዎችን ለመከለስ እና ሪፖርቶችን ለመፍጠር ደግሞ መመሪያ ይማሩ።",
  "support.hero.openChat": "ውይይት ክፈት",
  "support.hero.browse": "የምሳሌ ጥያቄዎችን ይመልከቱ",
  "support.quick.ask.title": "ለመረጃ ይጠይቁ",
  "support.quick.ask.text":
    "ጠቋሚዎችን፣ ቦታዎችንና የጊዜ ወቅቶችን ለመረመር ተፈጥሯዊ ቋንቋ ይጠቀሙ።",
  "support.quick.anomaly.title": "አኖማሊዎችን ይመልከቱ",
  "support.quick.anomaly.text": "የሪፖርት አማራጭዎችን ይረዱና ልዩነቶችን ይመርምሩ።",
  "support.quick.report.title": "ሪፖርቶችን ይሠርቱ",
  "support.quick.report.text":
    "የተቀመጡ የጤና አፈጻጸም ሪፖርቶችን ይፍጠሩና ወደ ውጭ ያውጡ።",
  "support.nav.onThisPage": "በዚህ ገጽ",
  "support.nav.gettingStarted": "መጀመር",
  "support.nav.chat": "ውይይቱን መጠቀም",
  "support.nav.examples": "የምሳሌ ጥያቄዎች",
  "support.nav.tips": "ምክሮች",
  "support.chat.title": "የውይይት በይነፍ",
  "support.chat.what.title": "ውይይት ምንነት አለው?",
  "support.chat.what.desc":
    "የውይይት በይነፍ በተፈጥሯዊ ቋንቋ የጤና መረጃን ለመጠየቅ ያስችልዎታል። ስለ ጠቋሚዎች፣ ክልሎች፣ የጊዜ ወቅቶችና ተቋማት ጥያቄዎችን ይጠይቁ — AI አገልጋዩ SQL ጥያቄዎችን በመስራት ውጤቶችን በማሳያዎች ይመልሳል።",
  "support.chat.step1.label": "ጥያቄዎን ይጻፉ",
  "support.chat.step1": "በዳችኛ ያለው ሳጥን ውስጥ ጥያቄዎን ይጻፉና Enter ይጫኑ ወይም Send ይጫኑ።",
  "support.chat.step2.label": "ውጤቶችን ይመልከቱ",
  "support.chat.step2":
    "ምላሾዎች ቻርቶች (አምድ፣ መስመር፣ ፓይ፣ ዶንት)፣ የመረጃ ሰንጠረዶችና KPI ካርዶችን ያካትታሉ።",
  "support.chat.step3.label": "የውይይት ታሪክ",
  "support.chat.step3":
    "የቀዳሚ ውይይቶች በጎኑ ውስጥ ይታያሉ። ለመጫን የሚፈልጉትን ይጫኑ።",
  "support.chat.tips.title": "ለተሻለ ውጤት ምክሮች",
  "support.chat.tip1.title": "ልዩ ይሁኑ",
  "support.chat.tip1.text":
    "\"የወባ መረጃ አሳይ\" ብለው ሳይጠይቁ \"በ2017 ዓ.ም. በኦሮሚያ የወባ ፖዚቲቪቲ ሬት ስንት?\" ማለት ይሞክሩ።",
  "support.chat.tip2.title": "ጠቋሚውን ያስታውሱ",
  "support.chat.tip2.text":
    "በጥያቄዎ ውስጥ የጠቋሚ ስም፣ ክልልና የጊዜ ወቅት ማካተት በጣም ትክክለኛ ውጤት ይሰጣል።",
  "support.chat.tip3.title": "ንጽጽሮችን ይጠይቁ",
  "support.chat.tip3.text":
    "\"በ2017 ዓ.ም. በክልሎች የወባ ጉዳዮችን ካነጻጸር\" ወይም \"ከፍተኛ የANC ሽፋን ያላቸው ወረዳዎች ትንተና፣\" ይሞክሩ።",
  "support.chat.tip4.title": "የቻርት ዓይነትን ይቀይሩ",
  "support.chat.tip4.text":
    "መልስ ከደረስዎ በኋላ የቻርት ዓይነት አሞጌዎች (አምድ፣ መስመር፣ ፓይ፣ ዶንት) በመጠቀም ማሳያውን ይቀይሩ።",

  // Support page - anomaly detection section
  "support.anomaly.title": "የአኖማሊ ለይቶ",
  "support.anomaly.sidebar.title": "የጎን መቆጣጠሪያዎች",
  "support.anomaly.sidebar.indicator.label": "ጠቋሚ",
  "support.anomaly.sidebar.indicator": "— ለመተንተን የጤና ጠቋሚውን ይምረጡ።",
  "support.anomaly.sidebar.hierarchy.label": "የድርሻ አዋራድ አዋራድ",
  "support.anomaly.sidebar.hierarchy":
    "— በክልል → ዞን → ወረዳ → PHCU → ደረጃ 6 → ደረጃ 7 በመራመድ የተወሰነውን ቦታ ያብሩ።",
  "support.anomaly.sidebar.run.label": "ትንተና አሂድ",
  "support.anomaly.sidebar.run": "— ለተመረጠው ወሰን አኖማሊዎችን ለመጣገም ይጫኑ።",
  "support.anomaly.severity.title": "አኖማሊዎችን ለመረዳት",
  "support.anomaly.severity.high.label": "ከፍተኛ አሰራር",
  "support.anomaly.severity.high": "— ከተጠበቁ ዋጋዎች ጠንካር ልዩነት። ወዲያውኑ አቅጣጫ ይፈልጋል።",
  "support.anomaly.severity.medium.label": "መካከለኛ አሰራር",
  "support.anomaly.severity.medium": "— መካከለኛ ልዩነት። ለመመርመር ያስገባል።",
  "support.anomaly.severity.low.label": "ዝቅተኛ አሰራር",
  "support.anomaly.severity.low": "— ትንሽ ልዩነት። የተጠበቀ ለውጥ ሊሆን ይችላል።",
  "support.anomaly.types.title": "የአኖማሊ ዓይነቶች",
  "support.anomaly.types.trend.label": "አዘጋጅነት",
  "support.anomaly.types.trend": "— በጊዜ ላይ የሚፈጠር ያላለፈ መጨመር ወይም መቀነስ ያሳያል።",
  "support.anomaly.types.rolling.label": "ተመንጋንዊ",
  "support.anomaly.types.rolling":
    "— ከቅርብ አማካኝዎች ጋር ተነጻጽሮ ድንጋጤ ለውጦችን ያሳያል።",
  "support.anomaly.types.seasonal.label": "ወቅታዊ",
  "support.anomaly.types.seasonal": "— ከወቅት አማራጭዎች የሚወለዱ ልዩነቶችን ያሳያል።",
  "support.anomaly.types.missing.label": "የጠፋተኛ መረጃ",
  "support.anomaly.types.missing": "— መረጃ የለላቸው ጊዜዎችን ያስታውቃል።",
  "support.anomaly.chart.title": "ቻርቱን እንዴት መቀበር",
  "support.anomaly.chart.intro": "የጊዜ አቋራጭ ቻርቱ የሚሠራቸውን ያሳያል፦",
  "support.anomaly.chart.observed.label": "የተመለከተ ዋጋ",
  "support.anomaly.chart.observed": "— ትክክለኛው የተመዘገበ ዋጋ።",
  "support.anomaly.chart.historical.label": "የታሪክ የተጠበቀ ዋጋ",
  "support.anomaly.chart.historical": "— ከያለፈባት አዘጋጅነት ጋር።",
  "support.anomaly.chart.rolling.label": "ተመንጋንዊ የተጠበቀ ዋጋ",
  "support.anomaly.chart.rolling": "— ከቅርብ አማካኝዎች ጋር።",
  "support.anomaly.chart.seasonal.label": "ወቅታዊ የተጠበቀ ዋጋ",
  "support.anomaly.chart.seasonal": "— ከወቅት አማራጭዎች ጋር።",
  "support.anomaly.breakdown.title": "የተቋም ማበረሳያ",
  "support.anomaly.breakdown.p1":
    "ለዚህ ጊዜ የተቋም ማበረሳያ ለመቀየር በጊዜ አቋራጭ ቻርቱ ላይ ያለውን ብቃት ያለ ነጥብ (የአኖማሊ ምልክት) ይጫኑ።",
  "support.anomaly.breakdown.p2":
    "ማበረሳያው በተመረጠው የድርሻ አዋራድ አዋራድ የሚያስተበቁ ሁሉንም ተቋማት በz-score መጠን ተደርገው ያሳያል።",
  "support.anomaly.filtering.title": "ማጣሪያና ማስወጋገያ",
  "support.anomaly.filtering.scenario.label": "የሁኔታ ማጣሪያ",
  "support.anomaly.filtering.scenario":
    "— የአኖማሊ ዓይነቶችን (አዘጋጅነት፣ ተመንጋንዊ፣ ወቅታዊ ወዘተ) ያስነሱ ወይም ያጥፉ።",
  "support.anomaly.filtering.severity.label": "የአሰራር ማጣሪያ",
  "support.anomaly.filtering.severity":
    "— ከፍተኛ፣ መካከለኛ ወይም ዝቅተኛ አሰራር ያላቸውን ብቻ ያሳዩ።",
  "support.anomaly.filtering.table.label": "የሰንጠረድ ማስተባበሪያ",
  "support.anomaly.filtering.table":
    "— ከቻርቱ ውስጥ ያለውን የአኖማሊ ሰንጠረድ ይከፍቱ ወይም ያጠፉ።",

  // Support page - report generator section
  "support.reports.title": "ሪፖርት ማመንጫ",
  "support.reports.template.title": "ጎን — የአብነት ምርጫ",
  "support.reports.template.yearly.label": "የጤና ሚኒስቴር ዓመታዊ",
  "support.reports.template.yearly.text": "— ዓመታዊ የጤና አፈጻጸም ሪፖርት አብነት።",
  "support.reports.template.quarterly.label": "የጤና ሚኒስቴር ወሩቃዊ",
  "support.reports.template.quarterly.text": "— ወሩቃዊ ሪፖርት አብነት።",
  "support.reports.template.monthly.label": "የጤና ሚኒስቴር ወርሃዊ",
  "support.reports.template.monthly.text": "— ወርሃዊ ሪፖርት አብነት።",
  "support.reports.params.title": "ጎን — መለኪያዎች",
  "support.reports.params.period.label": "ጊዜ",
  "support.reports.params.period.text":
    "— ዓመት፣ ሩብ (ለወሩቃዊ) ወይም ወር (ለወርሃዊ) ይምረጡ።",
  "support.reports.params.geography.label": "አካባቢ",
  "support.reports.params.geography.text":
    "— ብሔራዊ ወይም ክልላዊ ወሰን ይምረጡ። ለክልላዊ ከሆነ ተወሰነውን ክልል ይምረጡ።",
  "support.reports.geography.title": "ጎን — አካባቢ",
  "support.reports.geography.national.label": "ብሔራዊ",
  "support.reports.geography.national.text": "— ለሙሉ ሀገር ሪፖርት ይፈጥራል።",
  "support.reports.geography.regional.label": "ክልላዊ",
  "support.reports.geography.regional.text":
    "— ለተወሰነው ክልል (ለምሳሌ ኦሮሚያ፣ አማራ፣ ትግራይ) ሪፖርት ይፈጥራል።",
  "support.reports.generating.title": "ሪፖርት መፍጠር",
  "support.reports.generating.s1":
    "ከጎኑ አንድ አብነት (ዓመታዊ፣ ወሩቃዊ ወይም ወርሃዊ) ይምረጡ።",
  "support.reports.generating.s2": "የጊዜና የአካባቢ መለኪያዎችን ያስቀምጡ።",
  "support.reports.generating.s3":
    "በዳችኛ ያለው በጽሑፍ ሳጥን ውስጥ ተጨማሪ መመሪያዎችን ይጻፉ (ለምሳሌ \"ከትብብር ሽፋን ላይ ተጠቅም\")።",
  "support.reports.generating.s4":
    "ቋንቋ (እንግሊዝኛ ወይም አማርኛ)ና የውጤት ቅርጸት (DOCX ወይም PPTX) ይምረጡ።",
  "support.reports.generating.s5.label": "አመንጭ",
  "support.reports.generating.s5":
    "የሪፖርት ማስነድ ለመጀመር አመንጭ አሞጌዑን ይጫኑ (ወይም Enter ይጫኑ)።",
  "support.reports.improving.title": "ክፍሎችን ማሻሻል",
  "support.reports.improving.intro":
    "ሪፖርቱ ከተፈጠረ በኋላ ብቻ ክፍሎችን ማሻሻል ይችላሉ፦",
  "support.reports.improving.improve.label": "አሻሽል",
  "support.reports.improving.improve.text":
    "— ከክፍሉ ላይ በመሽከርክር እርምጣ \"አሻሽል\" በመጫን AI የመላጠጃውን እንዴት እንደሚጽረር መመሪያ ይስጡ።",
  "support.reports.improving.regenerate.label": "እንደገና አስነሳ",
  "support.reports.improving.regenerate.text":
    "— ከክፍሉ ላይ በመሽከርክር እርምጣ \"እንደገና አስነሳ\" በመጫን አዲስ ማጭሰሪያ ያግኙ።",
  "support.reports.exporting.title": "ሪፖርቶችን ማስወጋገር",
  "support.reports.exporting.docx.label": "DOCX",
  "support.reports.exporting.docx":
    "— ቻርቶች በምስል ተካተው ወደ Word ሰነድ ያስወጣል።",
  "support.reports.exporting.pptx.label": "PPTX",
  "support.reports.exporting.pptx":
    "— ለእያንዳንዱ ክፍል የሥላይድ ሃይሎች ያላቸው ወደ PowerPoint አቀራረብ ያስወጣል።",
  "support.reports.exporting.download.label": "አውርድ",
  "support.reports.exporting.download":
    "ለማስወጋገር በጎኑ ወይም በራሱ አሞጌ ላይ ያለውን አውርድ አሞጌ ይጫኑ።",

  // Report Guide Page
  "reportGuide.badge": "የሪፖርት ስቱዲዮ",
  "reportGuide.title": "የጤና መረጃን ሰዎች ሊጠቀሙበት ወደሚችሉበት ሪፖርት ይቀይሩ።",
  "reportGuide.subtitle":
    "የሪፖርት ማመንጫው አብነቶችን፣ የቀጥታ መረጃን፣ በAI የተጻፈ ትንተናን፣ ቻርቶችን፣ ማሻሻያን እና ወደ ውጭ መላክን እንዴት ወደ አንድ የሪፖርት አሰራር እንደሚቀላቅል ይማሩ።",
  "reportGuide.btn.back": "ወደ ሪፖርት ማመንጫ ተመለስ",
  "reportGuide.btn.workflow": "እንዴት እንደሚሰራ ይመልከቱ",
  "reportGuide.stats.reportRhythms.label": "የሪፖርት ምቶች",
  "reportGuide.stats.reportRhythms.text": "ዓመታዊ፣ ወሩቃዊ፣ ወርሃዊ",
  "reportGuide.stats.officeFormats.label": "የኦፊስ ቅርጸቶች",
  "reportGuide.stats.officeFormats.text": "DOCX እና PPTX",
  "reportGuide.stats.languages.label": "ቋንቋዎች",
  "reportGuide.stats.languages.text": "እንግሊዝኛ እና አማርኛ",
  "reportGuide.workflow.eyebrow": "የስራ ሂደት",
  "reportGuide.workflow.title": "ከሪፖርት ሃሳብ እስከ ተጠናቀቀ ሰነድ",
  "reportGuide.workflow.desc":
    "የስራ ቦታው መዋቅርን፣ ማስረጃን፣ ትረካን እና የአቀራረብ ደረጃዎችን የተያያዙ ያደርጋቸዋል።",
  "reportGuide.workflow.step1.title": "ቅርጹን ይምረጡ",
  "reportGuide.workflow.step1.text": "ዓመታዊ፣ ወሩቃዊ ወይም ወርሃዊ የሪፖርት አብነት ይምረጡ።",
  "reportGuide.workflow.step2.title": "ወሰኑን ያስቀምጡ",
  "reportGuide.workflow.step2.text": "የሪፖርት ጊዜውን እና ብሔራዊ ወይም ክልላዊ አካባቢን ይምረጡ።",
  "reportGuide.workflow.step3.title": "በመረጃ ያመነጩ",
  "reportGuide.workflow.step3.text":
    "ስርዓቱ የቀጥታ ጤና መረጃን ይፈልጋል እና የተመሰረቱ የክፍል ማጠቃለያዎችን ይጽፋል።",
  "reportGuide.workflow.step4.title": "ይገምግሙ እና ያጋሩ",
  "reportGuide.workflow.step4.text":
    "ክፍሎችን ያሻሽሉ፣ የቅርብ ስራዎችን እንደገና ይክፈቱ፣ እና የተቀነባበረ ሰነድ ወደ ውጭ ይላኩ።",
  "reportGuide.whyWorks.eyebrow": "ለምን እንደሚሰራ",
  "reportGuide.whyWorks.title": "በስሩ ማስረጃ ያለው የሪፖርት ስራ ቦታ",
  "reportGuide.whyWorks.feature1.title": "በመረጃ ቋት የተደገፈ",
  "reportGuide.whyWorks.feature1.text":
    "እያንዳንዱ ክፍል ከቀጥታ ጥያቄ ውጤቶች የተገነባ ነው። ባዶ ውጤቶች በፈጠራ ዋጋዎች ከመሞላት ይልቅ ይታለፋሉ።",
  "reportGuide.whyWorks.feature2.title": "በነባሪ ምስላዊ",
  "reportGuide.whyWorks.feature2.text":
    "ማመንጫው ጠቃሚ የቁጥር ውጤቶችን ወደ አምድ፣ መስመር ወይም ፓይ ቻርቶች ይቀይራል እና ደጋፊውን ሰንጠረዥ ዝግጁ ያደርጋል።",
  "reportGuide.whyWorks.feature3.title": "አንድን ክፍል ያሻሽሉ",
  "reportGuide.whyWorks.feature3.text":
    "የመጀመሪያውን ወሰን እና እውነተኛ መሠረት በመጠበቅ አንድን ክፍል ለማሻሻል የተፈጥሮ ቋንቋ መመሪያ ይስጡ።",
  "reportGuide.buildBrief.eyebrow": "መመሪያውን ይገንቡ",
  "reportGuide.buildBrief.title": "ለAI ጠቃሚ አቅጣጫ ይስጡ",
  "reportGuide.buildBrief.desc":
    "የሚያስቡትን ጠቋሚዎች፣ አካባቢ፣ ጊዜ እና ትኩረት ይጠቅሱ። የተመረጠው አብነት የሪፖርት መዋቅሩን ሲያቀርብ መመሪያዎ ትንተናውን ይመራዋል።",
  "reportGuide.buildBrief.example":
    "“በ2018 ዓ.ም. የሽፋን ለውጦችን እና የክልል ቅድሚያ የሚሰጣቸውን ነገሮች በማጉላት ለኦሮሚያ አጭር የእናትና ልጅ ጤና አፈጻጸም ሪፖርት አመንጭ።”",
  "reportGuide.buildBrief.controls.title": "ያሉ መቆጣጠሪያዎች",
  "reportGuide.buildBrief.controls.1": "ዓመታዊ፣ ወሩቃዊ ወይም ወርሃዊ አብነት",
  "reportGuide.buildBrief.controls.2": "የኢትዮጵያ ዓመት፣ ሩብ እና ወር",
  "reportGuide.buildBrief.controls.3": "ብሔራዊ ወይም ክልላዊ አካባቢ",
  "reportGuide.buildBrief.controls.4": "የእንግሊዝኛ ወይም የአማርኛ ውጤት",
  "reportGuide.buildBrief.controls.5": "የDOCX ወይም PPTX ኤክስፖርት",
  "reportGuide.makeSharper.eyebrow": "የበለጠ ግልጽ ያድርጉት",
  "reportGuide.makeSharper.title": "ነጠላ ክፍልን ያሻሽሉ",
  "reportGuide.makeSharper.desc":
    "አንድ አንቀጽ የበለጠ ግልጽ ትኩረት፣ አጭር ቋንቋ ወይም የተለየ ቻርት ሲያስፈልገው የክፍል ማሻሻያ ፓነሉን ይጠቀሙ። ጥያቄው የአሁኑን ይዘት እና ደጋፊ የሰንጠረዥ ረድፎችን ያካትታል፣ እና ያ ክፍል ብቻ ይለወጣል።",
  "reportGuide.makeSharper.goodInstructions.title": "ጥሩ የማሻሻያ መመሪያዎች",
  "reportGuide.makeSharper.goodInstructions.1":
    "ይህንን ለስራ አስፈጻሚ አዳማጭ የበለጠ አጭር ያድርጉት።",
  "reportGuide.makeSharper.goodInstructions.2": "በክልሎች መካከል ያሉትን ልዩነቶች አጉላ።",
  "reportGuide.makeSharper.goodInstructions.3": "አጭር፣ ሙያዊ ማጠቃለያ ይጠቀሙ።",
  "reportGuide.makeSharper.goodInstructions.4": "የዓመታዊ አዝማሚያውን ለማሳየት ቻርቱን ይቀይሩ።",
  "reportGuide.export.docx.title": "DOCX ለማርትዕ",
  "reportGuide.export.docx.desc":
    "ርዕስ፣ ጊዜ፣ አካባቢ፣ የተቁጠሩ ክፍሎች፣ ትረካ እና የተሳሉ ቻርቶች ያሉት ሊታረም የሚችል የኦፊስ ሰነድ ይፈጥራል።",
  "reportGuide.export.pptx.title": "PPTX ለማቅረብ",
  "reportGuide.export.pptx.desc":
    "አጭር ለስላይድ ዝግጁ ጽሑፍ፣ ቻርቶች፣ ሜታዳታ፣ ወጥ ዘይቤ፣ ግርጌዎች እና የገጽ ቁጥሮች ያሉት ሰፊ አቀራረብ ይፈጥራል።",
  "reportGuide.beforeShare.title": "ከማጋራትዎ በፊት",
  "reportGuide.beforeShare.desc":
    "ርዕሱን፣ ጊዜውን፣ አካባቢውን፣ የክፍል ሽፋኑን፣ የሰንጠረዥ ዋጋዎችን፣ የቻርት መለያዎችን፣ ቋንቋውን እና የመጨረሻውን የወጣ ሰነድ ያረጋግጡ። የተፈጠረው ይዘት በመረጃ የተደገፈ ነው፣ ነገር ግን ለኦፊሴላዊ ሪፖርት የሰው ልጅ ግምገማ አስፈላጊ ሆኖ ይቆያል።",
};

export const translations = { en, am } as const;
